import { useLatest } from '@qfa/react-components';
import { PropsWithChildren, createContext, useCallback, useContext, useState } from 'react';
import { PREFERENCE_KEYS } from '../../../types/preferenceTypes';
import { FilterInput } from '@kinetic-ui/data-layer';

type SessionFilterStorage = Partial<Record<PREFERENCE_KEYS, FilterInput[]>>;
type SessionFilterContextValue = {
  getSessionFilterValue: (key: PREFERENCE_KEYS) => FilterInput[] | undefined;
  setSessionFilterValue: (key: PREFERENCE_KEYS, value: FilterInput[]) => void;
};
const SessionFilterContext = createContext<SessionFilterContextValue>({
  setSessionFilterValue: () => {},
  getSessionFilterValue: () => undefined,
});

export const useSessionFilterContext = () => {
  const context = useContext(SessionFilterContext);
  if (!context) {
    throw new Error('useSessionFilterContext must be used within a SessionFilterContextProvider');
  }
  return context;
};

export const SessionFilterContextProvider = ({ children }: PropsWithChildren<{}>) => {
  const [sessionFilters, setSessionFilters] = useState<SessionFilterStorage>({});
  const valueRef = useLatest(sessionFilters);
  const setSessionFilterValue = useCallback((key: PREFERENCE_KEYS, value: any) => {
    setSessionFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const getSessionFilterValue = useCallback(
    (key: PREFERENCE_KEYS) => {
      return valueRef.current[key];
    },
    [valueRef]
  );

  return (
    <SessionFilterContext.Provider value={{ getSessionFilterValue, setSessionFilterValue }}>
      {children}
    </SessionFilterContext.Provider>
  );
};
